import { useMemo, useState } from 'react';
import { apiFetch, useApi } from '../hooks/useApi.js';

// Own Brand → Products we sell (Joe, 5 Nov 2026): what we sell and its price
// on every price list, all on one screen, changed in one go. "For sale" is
// what sales orders, order forms, price lists and discounts offer — not the
// category ("just because something is bottle finished doesn't mean it
// should be saleable").

const CAT = { bottle_finished: 'Bottle finished', bulk_finished: 'Bulk finished', semi_product: 'Semi product', by_product: 'By-product', service: 'Service', client_stock: 'Client stock' };
const num = (v) => (v === '' || v == null ? null : Number(v));

export default function ProductRange() {
  const { data, loading, refetch } = useApi('/price-lists/range', []);
  const items = data?.items || []; const lists = data?.lists || []; const base = data?.prices || {};
  const [show, setShow] = useState('sale'); // sale | all
  const [q, setQ] = useState('');
  const [sale, setSale] = useState({});   // id → bool (changed)
  const [price, setPrice] = useState({}); // `${id}|${list}` → string (changed)
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const forSale = (it) => (sale[it.id] ?? it.for_sale);
  const priceOf = (it, l) => { const k = `${it.id}|${l.id}`; return k in price ? price[k] : base[it.id]?.[l.id] ?? ''; };

  const shown = useMemo(() => {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    return items.filter((it) => (show === 'all' || it.for_sale || sale[it.id] !== undefined)
      && words.every((w) => `${it.name} ${it.sku || ''}`.toLowerCase().includes(w)));
  }, [items, show, q, sale]);

  // What's actually different from what's saved.
  const changes = useMemo(() => {
    const by = {};
    for (const [id, v] of Object.entries(sale)) { const it = items.find((x) => x.id === id); if (it && v !== it.for_sale) (by[id] ||= { stock_item_id: id }).for_sale = v; }
    for (const [k, v] of Object.entries(price)) {
      const [id, listId] = k.split('|'); const was = base[id]?.[listId] ?? null; const now = num(String(v).trim());
      if (now === was || (now != null && was != null && Math.abs(now - was) < 0.00005)) continue;
      ((by[id] ||= { stock_item_id: id }).prices ||= {})[listId] = String(v).trim() === '' ? null : String(v).trim();
    }
    return Object.values(by);
  }, [sale, price, items, base]);

  const setAll = (v) => setSale((s) => { const n = { ...s }; for (const it of shown) n[it.id] = v; return n; });

  async function saveAll() {
    setSaving(true); setMsg(null);
    try {
      await apiFetch('/price-lists/range', { method: 'PUT', body: { changes } });
      setMsg({ ok: true, text: `Saved ${changes.length} product${changes.length === 1 ? '' : 's'}` });
      setSale({}); setPrice({}); await refetch();
    } catch (e) { setMsg({ ok: false, text: e.message }); } finally { setSaving(false); }
  }

  const th = { textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)', whiteSpace: 'nowrap', background: 'var(--surface-2)', position: 'sticky', top: 0, zIndex: 1 };
  const td = { padding: '6px 10px', fontSize: 13.5, borderTop: '1px solid var(--border)', verticalAlign: 'middle' };
  const box = { width: 84, padding: '5px 7px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, textAlign: 'right', background: 'var(--surface)', color: 'var(--text)' };

  if (loading && !data) return <p style={{ color: 'var(--text-muted)' }}>Loading…</p>;
  return (
    <div>
      <p style={{ margin: '0 0 12px', fontSize: 13.5, color: 'var(--text-muted)', maxWidth: 760 }}>
        Tick what we sell — only those come up on sales orders, order forms, price lists and discounts. Type a price in a list's column to put a product on that list; clear it to take it off. Nothing changes until you press Save.
      </p>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a product" style={{ ...box, width: 240, textAlign: 'left', padding: '8px 10px', fontSize: 14 }} />
        {[['sale', 'For sale'], ['all', 'All our products']].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setShow(k)} style={{ padding: '7px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: `1px solid ${show === k ? 'var(--pld-green)' : 'var(--border)'}`, background: show === k ? 'var(--pld-green)' : 'var(--surface)', color: show === k ? '#fff' : 'var(--text)' }}>{label}</button>
        ))}
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {shown.length} shown · <button type="button" onClick={() => setAll(true)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--pld-green)', cursor: 'pointer', fontSize: 13 }}>tick all shown</button>
          {' · '}<button type="button" onClick={() => setAll(false)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--pld-green)', cursor: 'pointer', fontSize: 13 }}>untick all shown</button>
        </span>
      </div>

      <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'auto', maxHeight: '62vh' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={th}>For sale</th><th style={th}>Product</th><th style={{ ...th, textAlign: 'right' }}>In stock</th>
            {lists.map((l) => <th key={l.id} style={{ ...th, textAlign: 'right' }} title={l.use_gross_price ? 'Prices include VAT' : 'Prices ex VAT'}>{l.name}{l.use_gross_price ? ' (inc VAT)' : ''}{l.status === 'draft' ? ' · draft' : ''}</th>)}
          </tr></thead>
          <tbody>
            {shown.map((it) => {
              const on = forSale(it);
              return (
                <tr key={it.id} style={{ background: sale[it.id] !== undefined && sale[it.id] !== it.for_sale ? '#fffbeb' : 'transparent', opacity: on ? 1 : 0.7 }}>
                  <td style={{ ...td, textAlign: 'center', width: 70 }}><input type="checkbox" checked={on} aria-label={`Sell ${it.name}`} onChange={(e) => setSale((s) => ({ ...s, [it.id]: e.target.checked }))} /></td>
                  <td style={td}><div style={{ fontWeight: 600 }}>{it.name}</div><div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{it.sku}{CAT[it.category] && it.category !== 'bottle_finished' ? ` · ${CAT[it.category]}` : ''}</div></td>
                  <td style={{ ...td, textAlign: 'right', color: 'var(--text-muted)' }}>{Math.round(it.on_hand).toLocaleString()}</td>
                  {lists.map((l) => {
                    const k = `${it.id}|${l.id}`; const changed = k in price && num(String(price[k]).trim()) !== (base[it.id]?.[l.id] ?? null);
                    return (
                      <td key={l.id} style={{ ...td, textAlign: 'right' }}>
                        <input inputMode="decimal" value={priceOf(it, l)} placeholder="—" aria-label={`${it.name} on ${l.name}`}
                          onChange={(e) => setPrice((p) => ({ ...p, [k]: e.target.value }))}
                          style={{ ...box, borderColor: changed ? '#d97706' : 'var(--border)', background: changed ? '#fffbeb' : 'var(--surface)' }} />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {!shown.length && <tr><td colSpan={3 + lists.length} style={{ ...td, color: 'var(--text-muted)', textAlign: 'center', padding: 20 }}>{show === 'sale' ? 'Nothing ticked as for sale yet — show All our products to tick some.' : 'Nothing matches.'}</td></tr>}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'flex-end', marginTop: 12, position: 'sticky', bottom: 0, background: 'var(--bg, transparent)', padding: '8px 0' }}>
        {msg && <span style={{ fontSize: 13, color: msg.ok ? '#166534' : '#b91c1c' }}>{msg.text}</span>}
        {changes.length > 0 && <button type="button" onClick={() => { setSale({}); setPrice({}); setMsg(null); }} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}>Undo changes</button>}
        <button type="button" disabled={!changes.length || saving} onClick={saveAll}
          style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: changes.length ? 'var(--pld-green)' : 'var(--border)', color: '#fff', cursor: changes.length ? 'pointer' : 'default', fontSize: 13.5, fontWeight: 600 }}>
          {saving ? 'Saving…' : changes.length ? `Save ${changes.length} change${changes.length === 1 ? '' : 's'}` : 'No changes'}
        </button>
      </div>
    </div>
  );
}
